import React, { useState, useEffect } from "react"
import gql from "graphql-tag"
import { Modal, ModalBody, Button, UncontrolledTooltip, Progress, ButtonGroup } from "reactstrap"
import { checkAllInputsFilled, checkAllInputsValid, handleDriverDataChange, validateDocumentData } from "./utils"
import { ReactComponent as DocVerified } from "../../assets/images/icons/doc-verified.svg"
import { isImage, isHeicFile } from "./DocumentsListNew"
import { HasPermissionsQuery } from "../../Functions/querys"
import UpdateDocumentModal from "./UpdateDocumentModal"
import ConfirmationPopup from "../ConfirmationPopup"
import DocumentDetailsPopover from "./DocumentDetailsPopover"
import DocumentHistoryPopover from "./DocumentHistoryPopover"
import DocumentFields from "./DocumentFields"
import "./DocumentListNew.css"
import { graphql } from '@apollo/client/react/hoc'
import withApolloClient from '../../withApolloClient'

const DriverDocumentDataQuery = gql`
  query DriverDocumentData($driverId: ID!) {
    driverDocumentData(driverId: $driverId) {
      documentData {
        documentName
        requiredData {
          fieldCode
          fieldType
          displayName
          value
          dataCategory
        }
      }
      country
    }
  }
`

const ExecuteOCRMutation = gql`
  mutation ExecuteOCR($input: ExecuteOCRInput!) {
    executeOcr(input: $input) {
      ok
      errors {
        messages
      }
    }
  }
`

const DeleteDriverDocumentMutation = gql`
  mutation deleteDriverDocumentMutation($input: DeleteDriverDocumentMutationInput!) {
    deleteDriverDocument(input: $input) {
      ok
      errors {
        messages
      }
    }
  }
`

const UpdateDocumentMutation = gql`
  mutation updateDocumentMutation($input: UpdateDocumentMutationInput!) {
    updateDocument(input: $input) {
      ok
      errors {
        messages
      }
    }
  }
`
const UpdateDocumentFieldsMutation = gql`
  mutation updateDocumentFields($input: UpdateDocumentFieldsInput!) {
    updateDocumentFields(input: $input) {
      ok
      errors {
        messages
      }
    }
  }
`

const UpdateIncidentDocumentMutation = gql`
  mutation updateIncidentDocument($input: UpdateIncidentDocumentMutationInput!) {
    updateIncidentDocument(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const SendDocumentToDriverMutation = gql`
  mutation sendDocumentToDriver($input: SendDocumentToDriverInput!) {
    sendDocumentToDriver(input: $input) {
      ok
      errors {
        messages
      }
    }
  }
`


function DocumentModalNew({
    currentUser,
    handleClose,
    next,
    prev,
    objectType = "driver",
    driverId,
    document: doc,
    driverDocumentData,
    apolloClient,
    isPicture,
    refetch,
    hasPermissions,
    driverDataLoading,
    permissionsLoading,
    allowSendDocToDriver,
    documentCategory,
}) {
    const [modal, setModal] = useState(true)
    const [rotation, setRotation] = useState(0)
    const [scale, setScale] = useState(1)
    const [documentTypeDocumentData, setDocumentTypeDocumentData] = useState([])
    const [allInputsFilled, setAllInputsFilled] = useState(false)
    const [allInputsValid, setAllInputsValid] = useState(false)
    const [ocrError, setOcrError] = useState("")
    const [ocrSuccess, setOcrSuccess] = useState("")
    const [deleteError, setDeleteError] = useState("")
    const [updateError, setUpdateError] = useState("")
    const [operationType, setOperationType] = useState("")
    const [loading, setLoading] = useState(false)
    const [openModal, setOpenModal] = useState("")
    const [sendPdfLoading, setSendDocLoading] = useState(false)
    const [sendPdfError, setSendDocError] = useState("")
    const [sendPdfSuccess, setSendDocSuccess] = useState("")
    const selectedDocumentType = doc && doc.documentType ? doc.documentType : ""
    const documentHasAnyFields = documentTypeDocumentData.length > 0
    const isIncident = objectType === "incident"

    const displayUrl = doc?.documentUrl
    const isConvertedPreview = !!doc?.__isConvertedPreview
    const isUnconvertedHeic = isHeicFile(doc?.name) && !isConvertedPreview
    const shouldRenderAsImage = isConvertedPreview || isImage(displayUrl, doc?.name)

    useEffect(() => {
        if (selectedDocumentType && driverDocumentData?.documentData) {
            const initialData = validateDocumentData(driverDocumentData, selectedDocumentType)
            const updatedDocumentData = driverDocumentData.country
                ? initialData.map((item) =>
                    item.fieldCode.startsWith("COUNTRY")
                        ? { ...item, value: driverDocumentData.country }
                        : item
                )
                : initialData
            setDocumentTypeDocumentData(updatedDocumentData)
            const allFilled = checkAllInputsFilled(updatedDocumentData)
            setAllInputsFilled(allFilled)
            const allValid = checkAllInputsValid(updatedDocumentData)
            setAllInputsValid(allValid)
        }
    }, [driverDocumentData, selectedDocumentType])

    useEffect(() => {
        if (!modal) return

        const handleKeyPress = (event) => {
            if (event.keyCode === 39) return next && next()
            if (event.keyCode === 37) return prev && prev()
        }

        window.addEventListener("keydown", handleKeyPress)

        return () => {
            window.removeEventListener("keydown", handleKeyPress)
        }
    }, [modal, next, prev])

    const toggleModal = () => setModal(!modal)
    const rotateDoc = () => setRotation((prev) => (prev + 90) % 360)
    const scaleDoc = (increment) => setScale((prev) => Math.max(prev + increment, 0.1))

    const mapFieldsForUpdate = (fields) => {
        return fields.map(({ fieldCode, dataCategory, value }) => ({
            fieldCode,
            dataCategory,
            value: typeof value === 'string' ? value.trim() : value,
        }))
    }

    const handleUpdateDocumentFields = async () => {
        if (!doc?.id || !documentTypeDocumentData?.length) {
            return
        }

        setUpdateError("")

        try {
            setLoading(true)
            const input = {
                documentId: doc.id,
                updatedFields: mapFieldsForUpdate(documentTypeDocumentData),
            }

            const result = await apolloClient.mutate({
                mutation: UpdateDocumentFieldsMutation,
                variables: { input },
                refetchQueries: [{
                    query: DriverDocumentDataQuery,
                    variables: { driverId }
                }],
            })

            const response = result?.data?.updateDocumentFields
            if (response?.ok) {
                setUpdateError("")
                refetch && refetch()
                handleCloseModal()
            } else {
                const errorMsg = response?.errors?.[0]?.messages?.[0] ?? "Unknown error updating fields."
                setUpdateError(errorMsg)
            }
        } catch (error) {
            setUpdateError(error.message)
        } finally {
            setLoading(false)
        }
    }

    const downloadDoc = (url) => {
        // TODO: Add download functionality
        window.open(url, "_blank")
    }

    const isUnsupportedHeic = (doc) => {
        const isHeic = isHeicFile(doc?.name, doc?.documentUrl)
        const isConvertedPreview = !!doc?.__isConvertedPreview
        return isHeic && !isConvertedPreview
    }

    const printDoc = (url, doc) => {
        if (isUnsupportedHeic(doc)) return
        var printWindow = window.open(url, "Print", "width=800,height=600")
        printWindow.print()
    }

    const handleCloseModal = () => {
        handleClose && handleClose()
    }

    const handleClosePopover = () => {
        setOpenModal("")
    }

    const handleFieldChange = (input) => {
        handleDriverDataChange(
            input,
            documentTypeDocumentData,
            selectedDocumentType?.typeName,
            (updatedData) => {
                setDocumentTypeDocumentData(updatedData)
                const allFilled = checkAllInputsFilled(updatedData)
                setAllInputsFilled(allFilled)
                const allValid = checkAllInputsValid(updatedData)
                setAllInputsValid(allValid)
            }
        )
    }

    const handleExecuteOCR = async (id, forceVerify = false) => {
        if (!id) {
            setOcrError("Document ID is required for OCR.")
            return
        }

        try {
            setOcrError("")
            setOcrSuccess("")
            const { data } = await apolloClient.mutate({
                mutation: ExecuteOCRMutation,
                variables: { input: { documentId: id, forceVerify } },
            })

            if (data.executeOcr.ok) {
                setOcrSuccess("OCR executed successfully.")
            } else {
                setOcrError(data.executeOcr.errors?.[0]?.messages?.[0] ?? "An error occurred.")
            }
        } catch (error) {
            setOcrError(`Error executing OCR: ${error.message}`)
        }
    }

    const handleDocumentToDriver = async () => {
        setSendDocError("")
        setSendDocSuccess("")
        setSendDocLoading(true)
        try {
            if (!doc?.documentUrl || !doc?.name || !driverId) {
                setSendDocError("Missing document file, file name, or driver ID.")
                setSendDocLoading(false)
                return
            }
            let fileBase64 = doc.documentUrl
            if (fileBase64.startsWith("data:application/pdf;base64,")) {
                fileBase64 = fileBase64.split(",")[1]
            }
            const input = {
                driverId,
                fileBase64,
                fileName: doc.name,
            }
            const result = await apolloClient.mutate({
                mutation: SendDocumentToDriverMutation,
                variables: { input },
            })
            const response = result?.data?.sendDocumentToDriver
            if (response?.ok) {
                setSendDocSuccess("PDF sent to driver successfully.")
            } else {
                const errorMsg = response?.errors?.[0]?.messages?.[0] ?? "Unknown error sending PDF."
                setSendDocError(errorMsg)
            }
        } catch (error) {
            setSendDocError(error.message)
        } finally {
            setSendDocLoading(false)
        }
    }

    const documentHandler = async (id, action) => {
        if (!id || !action) return

        try {
            setLoading(true)
            setDeleteError("")

            if (action === "update") {
                await handleUpdateDocumentFields()
                return
            }

            const isIncidentArchiveAction = isIncident && ["archive", "unarchive"].includes(action)
            const input = isIncidentArchiveAction
                ? {
                    incidentDocumentId: id,
                    isArchived: action === "archive",
                }
                : {
                    delete: { documentId: id },
                    archive: { id, isArchived: true },
                    unarchive: { id, isArchived: false },
                }[action] || {}

            if (!Object.keys(input).length) {
                throw new Error(`Unsupported action: ${action}`)
            }

            const mutation = isIncidentArchiveAction
                ? UpdateIncidentDocumentMutation
                : {
                    delete: DeleteDriverDocumentMutation,
                    archive: UpdateDocumentMutation,
                    unarchive: UpdateDocumentMutation,
                }[action]

            const result = await apolloClient.mutate({
                mutation,
                variables: { input },
            })
            refetch && refetch()
            const responseKey = isIncidentArchiveAction
                ? "updateIncidentDocument"
                : {
                    delete: "deleteDriverDocument",
                    archive: "updateDocument",
                    unarchive: "updateDocument",
                }[action]

            let responseData = result?.data?.[responseKey]

            if (responseData?.ok) {
                handleCloseModal()
            } else {
                const errorMessage = responseData?.errors?.[0]?.messages?.[0] ?? "An error occurred."
                setDeleteError(errorMessage)
            }
        } catch (error) {
            setDeleteError(`An error occurred: ${error.message}`)
        } finally {
            setLoading(false)
        }
    }

    const fetchMetaData = (metadata = null, name) => {
        if (!metadata || metadata === "") return
        try {
            metadata = JSON.parse(metadata.replace(/'/g, '"').replace(/True/g, 'true').replace(/False/g, 'false'))
        } catch {
            metadata = null
        }
        return metadata?.[name] ?? null
    }


    if (driverDataLoading || permissionsLoading) {
        return (
            <Modal isOpen={modal} toggle={toggleModal} fullscreen>
                <ModalBody>
                    <Progress animated color="info" value={100} />
                </ModalBody>
            </Modal>
        )
    }

    return (
        <Modal className="DocViewerModal" isOpen={modal} toggle={toggleModal} onClosed={handleCloseModal} fullscreen>
            {!(doc?.documentUrl) ?
                <div>No document passed</div> :
                <>
                    {operationType && ["delete", "archive", "unarchive", "update"].includes(operationType) && (
                        <ConfirmationPopup
                            open={!!operationType}
                            message={`Are you sure you want to ${operationType} this document?`}
                            loading={loading}
                            handleClose={() => setOperationType("")}
                            action="Yes"
                            confirmAction={() => documentHandler(doc.id, operationType)}
                            color="danger"
                            error={deleteError || updateError || ""}
                        />
                    )}
                    {openModal === "UpdateDocumentModal" &&
                        <UpdateDocumentModal
                            currentUser={currentUser}
                            selectedDocId={doc?.id}
                            handleClose={handleClosePopover}
                            open={openModal === "UpdateDocumentModal"}
                            target="UpdateDocumentButton"
                            objectType={objectType}
                            documentCategory={documentCategory}
                            refetchQuery={refetch}
                            driverId={driverId}
                        />
                    }
                    {openModal === "DocumentDetailsPopover" &&
                        <DocumentDetailsPopover
                            isOpen={openModal === "DocumentDetailsPopover"}
                            target="DocumentDetailsPopover"
                            doc={doc}
                            objectType={objectType}
                            handleClosePopover={handleClosePopover}
                        />
                    }
                    {openModal === "documentHistory" &&
                        <DocumentHistoryPopover
                            isOpen={openModal === "documentHistory"}
                            target="documentHistory"
                            doc={doc}
                            handleClosePopover={handleClosePopover}
                        />
                    }
                    <div className="DocViewerModalHeader">
                        <p className="DocViewerModalTitle">
                            {doc?.documentType?.typeName || doc?.name || "Document Name"} &nbsp;
                            {doc?.isVerified && <>
                                <UncontrolledTooltip target="verified">{"Verified By " + (fetchMetaData(doc?.metadata, "verified_by") || "System")}</UncontrolledTooltip>
                                {fetchMetaData(doc.metadata, "verified_by") !== "System" ? <DocVerified id="verified" width="20" height="20" className="fa fa-check-circle" fill="green" /> : <i id="verified" className="fa fa-check-circle" style={{ color: "green" }}></i>}
                            </>}
                        </p>
                        <div className="DocViewerModalActions">
                            <p onClick={rotateDoc}><i className="fa fa-rotate-right"></i></p>
                            <p onClick={() => scaleDoc(0.1)}><i className="fa fa-search-plus"></i></p>
                            <p onClick={() => scaleDoc(-0.1)}><i className="fa fa-search-minus"></i></p>
                            <p onClick={() => downloadDoc(doc.documentUrl)}><i className="fa fa-download"></i></p>
                            <p onClick={() => printDoc(doc.documentUrl, doc)}><i className="fa fa-print"></i></p>
                            <p><i className="fa fa-times" onClick={handleCloseModal}></i></p>
                        </div>
                    </div>
                    <ModalBody>
                        {ocrError && <div style={{ color: "red" }}>{ocrError}</div>}
                        {ocrSuccess && <div style={{ color: "green" }}>{ocrSuccess}</div>}
                        {deleteError && <div style={{ color: "red" }}>{deleteError}</div>}
                        {sendPdfError && <div style={{ color: "red" }}>{sendPdfError}</div>}
                        {sendPdfSuccess && <div style={{ color: "green" }}>{sendPdfSuccess}</div>}
                        {document ? (
                            shouldRenderAsImage
                                ? <img style={{ transform: `rotate(${rotation}deg) scale(${scale})` }} className="DocViewerImage" src={displayUrl} alt="Document" />
                                : isHeicFile(doc?.name, displayUrl) && isUnconvertedHeic ? (<div className="DocPreviewNotSupported"><p>HEIC preview not supported in this browser</p></div>)
                                    : (<iframe scrolling="yes" className="DocViewerFrame" src={displayUrl} title="Document" />)
                            // : <iframe scrolling="yes" className="DocViewerFrame" src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(displayUrl)}`} title="Document" />
                        ) : <p>No document available.</p>}
                        <DocumentFields
                            documentTypeDocumentData={documentTypeDocumentData}
                            allInputsFilled={allInputsFilled}
                            handleFieldChange={handleFieldChange}
                        />
                    </ModalBody>
                    <div className="DocViewerModalFooter">
                        <ButtonGroup>
                            <Button className="btn btn-dark" onClick={prev}><i className="fa fa-chevron-left"> Prev</i></Button>
                            <Button className="btn btn-dark" onClick={next}>Next <i className="fa fa-chevron-right"></i></Button>
                        </ButtonGroup>
                        <div className="DocViewerFooterAction">
                            <Button className="btn btn-secondary" id="DocumentDetailsPopover" onClick={() => setOpenModal("DocumentDetailsPopover")}><i className="fa fa-info-circle"></i> Details</Button>
                            {doc.history && doc.history.length > 0 && <Button className="btn btn-secondary" style={{ background: "#008B8B" }} id="documentHistory" onClick={() => setOpenModal("documentHistory")}><i className="fa fa-history"></i> History</Button>}
                            {allowSendDocToDriver &&
                                <Button
                                    color="info"
                                    onClick={handleDocumentToDriver}
                                    disabled={sendPdfLoading}
                                >
                                    <i className="fa fa-envelope-o" aria-hidden="true"></i>&nbsp;{sendPdfLoading ? "Sending Doc..." : "Send Doc to Driver"}
                                </Button>
                            }
                            {!isPicture && (
                                <>
                                    {doc?.isArchived && (
                                        <Button className="btn btn-danger" onClick={() => setOperationType("unarchive")}>
                                            <i className="fa fa-file-archive-o" aria-hidden="true"></i>
                                            &nbsp;Unarchive
                                        </Button>
                                    )}
                                    {!doc?.isArchived && (
                                        <Button className="btn btn-danger" onClick={() => setOperationType("archive")}>
                                            <i className="fa fa-trash"></i>
                                            &nbsp;Archive
                                        </Button>
                                    )}
                                    {doc?.isArchived && hasPermissions?.includes("delete_driverdocument") &&
                                        objectType === "driver" && (
                                            <Button className="btn btn-danger" onClick={() => setOperationType("delete")}>
                                                <i className="fa fa-trash"></i>
                                                &nbsp;Delete
                                            </Button>
                                        )}
                                    <Button className="btn btn-warning" id="UpdateDocumentButton" onClick={() => setOpenModal("UpdateDocumentModal")}>
                                        <i className="fa fa-pencil-square"></i>
                                        &nbsp;Edit
                                    </Button>
                                    {documentHasAnyFields && (
                                        <Button className="btn btn-warning" onClick={() => setOperationType("update")} disabled={!allInputsFilled || !allInputsValid || loading}>
                                            <i className="fa fa-save"></i>
                                            &nbsp;Update info
                                        </Button>
                                    )}
                                </>
                            )}
                            {!isIncident && ["TLC", "Driver License", "Constancia de Situacion Fiscal"].includes(doc?.documentType?.typeName ?? "") && (
                                <>
                                    <Button className="btn btn-info" onClick={() => handleExecuteOCR(doc?.id)}>
                                        <i className="fa fa-id-card"></i> Doc Validation
                                    </Button>
                                    {hasPermissions?.includes("custom_force_run_ocr") && (
                                        <Button className="btn btn-info" onClick={() => handleExecuteOCR(doc?.id, true)}>
                                            <i className="fa fa-id-card"></i> Manual Doc Validation
                                        </Button>
                                    )}
                                </>
                            )}
                            <Button className="btn btn-dark" onClick={handleCloseModal}><i className="fa fa-times"></i> Close</Button>
                        </div>
                    </div>
                </>
            }
        </Modal>
    )
}

export default graphql(HasPermissionsQuery, {
    options: () => ({
        variables: { userPermissions: ["custom_force_run_ocr", "delete_driverdocument"] },
        fetchPolicy: "cache-first",
    }),
    props: ({ data: { hasPermissions, loading, variables } }) => (
        { hasPermissions, loading, variables }
    )
})(
    graphql(DriverDocumentDataQuery, {
        options: ({ driverId }) => ({
            skip: !driverId,
            variables: { driverId },
            fetchPolicy: "cache-first",
            notifyOnNetworkStatusChange: true,
        }),
        props: ({ data }) => {
            if (!data) {
                return { driverDataLoading: false, driverDocumentData: null }
            }
            const { loading: driverDataLoading, driverDocumentData } = data
            return { driverDataLoading, driverDocumentData }
        }
    })(withApolloClient(DocumentModalNew))
)
